import type { IBrand } from 'data/schema';
import { sendApiRequest } from 'utils/network';

export interface IResolverParams {
  efficiencyClass: string | undefined;
  locale: string;
}

export type TBrandResolver = (params: IResolverParams) => Promise<string | null>;

const brandResolvers = new Map<IBrand, TBrandResolver>();

const urlCache = new Map<string, string | null>();
const inFlight = new Map<string, Promise<string | null>>();

export async function getBrandEnergyLabelUrl(params: IResolverParams): Promise<string | null> {
  const { efficiencyClass, locale } = params;

  if (!efficiencyClass) {
    return null;
  }

  const cacheKey = `${efficiencyClass}:${locale}`;
  if (urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey) ?? null;
  }

  const existing = inFlight.get(cacheKey);
  if (existing) {
    return existing;
  }

  const promise = sendApiRequest<{ url: string }>(
    `/energy-label?efficiencyClass=${encodeURIComponent(efficiencyClass)}&locale=${encodeURIComponent(locale)}`,
    { suppressToast: true }
  )
    .then((result) => {
      const url = result?.url ?? null;
      urlCache.set(cacheKey, url);
      return url;
    })
    .catch(() => {
      urlCache.set(cacheKey, null);
      return null;
    })
    .finally(() => {
      inFlight.delete(cacheKey);
    });

  inFlight.set(cacheKey, promise);
  return promise;
}

export function registerBrandResolver(brand: IBrand, resolver: TBrandResolver): void {
  brandResolvers.set(brand, resolver);
}

export async function resolveLabelUrl(brand: IBrand, params: IResolverParams): Promise<string | null> {
  const resolver = brandResolvers.get(brand) ?? getBrandEnergyLabelUrl;
  return resolver(params);
}

export function resetResolverRegistry(): void {
  brandResolvers.clear();
}

export function invalidateCacheEntry(efficiencyClass: string, locale: string): void {
  urlCache.delete(`${efficiencyClass}:${locale}`);
}

export function resetUrlCache(): void {
  urlCache.clear();
  inFlight.clear();
}
